import { Copy, Mail, X } from "lucide-mui";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api, type Invitation } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";
import { useOrgInvitations } from "../hooks/resources.ts";
import { copyToClipboard } from "../utils/clipboard.ts";
import { formatDate } from "../utils/format.ts";
import IdentityCell from "./IdentityCell.tsx";
import { invitationLink } from "./OrgInviteDialog.tsx";
import { OrgRoleChips } from "./OrgRoles.tsx";
import RelativeTime from "./RelativeTime.tsx";
import RowActions from "./RowActions.tsx";
import SectionHeading from "./SectionHeading.tsx";
import StatusChip from "./StatusChip.tsx";

const DAY_MS = 24 * 60 * 60 * 1000;

function Expiry({ expiresAt }: { expiresAt: string }) {
  const msLeft = new Date(expiresAt).getTime() - Date.now();
  if (msLeft <= 0) return <Chip size="small" color="warning" label="Expired" />;
  const days = Math.ceil(msLeft / DAY_MS);
  return (
    <Tooltip title={formatDate(expiresAt, "full")}>
      <span>{days === 1 ? "expires within a day" : `expires in ${days} days`}</span>
    </Tooltip>
  );
}

/** The invitation rows themselves, for a box the caller draws (a `Paper`, an `AccordionCard`). */
export function OrgInvitationRows({
  organizationId,
  invitations,
}: {
  organizationId: string;
  invitations: Invitation[];
}) {
  const queryClient = useQueryClient();

  const cancel = useMutation({
    mutationFn: (inv: Invitation) => api.cancelOrgInvitation(organizationId, inv.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.orgInvitation.byOrg(organizationId) });
      toast.success("Invitation cancelled");
    },
  });

  return (
    <>
      {invitations.map((inv, i) => (
        <Box
          key={inv.id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1,
            borderTop: i ? 1 : 0,
            borderColor: "divider",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <IdentityCell
              avatar={
                <Avatar sx={{ width: 32, height: 32, bgcolor: "action.selected", color: "text.secondary" }}>
                  <Mail fontSize="small" />
                </Avatar>
              }
              name={inv.email}
              caption={
                <>
                  Invited <RelativeTime value={inv.created_at} />
                  {" · "}
                  <Expiry expiresAt={inv.expires_at} />
                </>
              }
            />
          </Box>
          {inv.email_status && inv.email_status !== "canceled" && (
            <StatusChip kind="invitation_email" value={inv.email_status} />
          )}
          <OrgRoleChips roles={inv.org_roles} />
          <Button
            size="small"
            startIcon={<Copy />}
            onClick={() => void copyToClipboard(invitationLink(inv.token), "Link copied")}
            sx={{ flexShrink: 0 }}
          >
            Copy link
          </Button>
          <RowActions
            label={`the invitation to ${inv.email}`}
            actions={[
              {
                label: "Cancel invitation",
                icon: <X fontSize="small" />,
                destructive: true,
                onClick: () => cancel.mutateAsync(inv),
                confirm: {
                  title: "Cancel invitation",
                  confirmLabel: "Cancel invitation",
                  cancelLabel: "Keep",
                  body: (
                    <>
                      Cancel the invitation for <strong>{inv.email}</strong>? Its link stops working.
                    </>
                  ),
                },
              },
            ]}
          />
        </Box>
      ))}
    </>
  );
}

/**
 * The organization's invitations that haven't been accepted yet, expired ones
 * included. Renders nothing while there are none.
 */
function OrgInvitationsList({ organizationId }: { organizationId: string }) {
  const { data: invitations = [] } = useOrgInvitations(organizationId);

  if (invitations.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <SectionHeading title="Pending invitations" count={invitations.length} />
      <Paper variant="outlined">
        <OrgInvitationRows organizationId={organizationId} invitations={invitations} />
      </Paper>
    </Box>
  );
}

export default OrgInvitationsList;
